import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";

const images = Array.from({ length: 19 }, (_, i) => `/assets/achievements/page-${String(i + 1).padStart(2, "0")}.jpg`);

export const AchievementGallery = () => {
  const [open, setOpen] = useState(null); // index or null

  const close = useCallback(() => setOpen(null), []);
  const prev = useCallback(() => setOpen((i) => (i - 1 + images.length) % images.length), []);
  const next = useCallback(() => setOpen((i) => (i + 1) % images.length), []);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, prev, next]);

  return (
    <div className="mt-14 sm:mt-16">
      <div className="flex items-center gap-3 mb-8">
        <Maximize2 size={20} className="text-gold" />
        <h3 className="font-display text-xl sm:text-2xl font-bold">Awards & Certificates Gallery</h3>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((src, i) => (
          <motion.button
            key={src}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
            onClick={() => setOpen(i)}
            data-testid={`gallery-thumb-${i}`}
            className="group relative aspect-[3/4] rounded-xl overflow-hidden bg-white border border-white/10 hover:border-gold/60 transition-all duration-300"
          >
            <img
              src={src}
              alt={`Certificate ${i + 1}`}
              loading="lazy"
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute inset-0 bg-navy/0 group-hover:bg-navy/30 transition-colors flex items-center justify-center">
              <Maximize2 size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </span>
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-navy/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={close}
              data-testid="lightbox-close"
              className="absolute top-4 right-4 sm:top-6 sm:right-6 h-11 w-11 rounded-full bg-white/10 hover:bg-gold hover:text-navy text-white flex items-center justify-center transition-colors z-10"
            >
              <X size={22} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              data-testid="lightbox-prev"
              className="absolute left-2 sm:left-6 h-11 w-11 rounded-full bg-white/10 hover:bg-gold hover:text-navy text-white flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              data-testid="lightbox-next"
              className="absolute right-2 sm:right-6 h-11 w-11 rounded-full bg-white/10 hover:bg-gold hover:text-navy text-white flex items-center justify-center transition-colors"
            >
              <ChevronRight size={24} />
            </button>
            <motion.img
              key={open}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              src={images[open]}
              alt={`Certificate ${open + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[88vh] max-w-[90vw] sm:max-w-[800px] object-contain rounded-lg shadow-2xl bg-white"
            />
            <span className="absolute bottom-5 left-1/2 -translate-x-1/2 text-bone/70 text-sm font-plex">
              {open + 1} / {images.length}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
